/**
 * Everything still standing between this customer and go-live.
 *
 * Computed, never ticked. The page this replaces had TWO go-live checklists —
 * one inline on the organization page and one on `/console/operations/go-live`
 * — computed separately, reading MFA enrollment from two different paths, and
 * therefore able to disagree about whether the System Admin was set up. There
 * is one answer now and every stage is a view of it.
 *
 * Blocking and advisory are separated because the distinction is the whole
 * point: an add-on nobody bought is incomplete and is not a reason to hold a
 * hospital's policy library back.
 */
import page from '../../../../shared/Page.module.css';
import { RequirementList } from '../RequirementList';
import type { Onboarding, StageId } from '../types';
import { GroupCard } from '../../../../shared/GroupCard';

export function ReadinessStage({
  data, onGo,
}: {
  data: Onboarding; onGo: (stage: StageId) => void;
}) {
  const all = data.stages.flatMap((s) => s.requirements);
  const blocking = all.filter((r) => r.blocking && r.status !== 'met');
  const advisory = all.filter((r) => !r.blocking && r.status === 'warning');
  const done = all.filter((r) => r.status === 'met');

  return (
    <>
      <div className={`${page.notice} ${blocking.length === 0 ? page.noticeInfo : page.noticeWarn}`}>
        {blocking.length === 0
          ? <><strong>Nothing is blocking go-live.</strong> Every required item is done.</>
          : <><strong>{blocking.length} blocking {blocking.length === 1 ? 'item' : 'items'}.</strong>{' '}
            Each one has to be settled before this organization can go live.</>}
      </div>

      {blocking.length > 0 && (
        <GroupCard title="Blocking" meta={`${blocking.length}`} list>
          <RequirementList
            requirements={blocking}
            action={(r) => (
              <button type="button" className={page.btn} onClick={() => onGo(r.stage)}>
                Open {data.stages.find((s) => s.id === r.stage)?.title ?? 'stage'}
              </button>
            )}
          />
        </GroupCard>
      )}

      {advisory.length > 0 && (
        <GroupCard title="Worth doing, not blocking" meta={`${advisory.length}`} list>
          <p className={page.itemMeta} style={{ paddingTop: 'var(--sp-3)' }}>
            None of these holds go-live. They are here so nobody has to wonder whether they were missed.
          </p>
          <RequirementList
            requirements={advisory}
            action={(r) => (
              <button type="button" className={page.btn} onClick={() => onGo(r.stage)}>Open</button>
            )}
          />
        </GroupCard>
      )}

      <GroupCard title="Already done" meta={`${done.length}`} list>
        <RequirementList requirements={done} />
      </GroupCard>
    </>
  );
}
